/* Prepared integration. Activated only by an explicitly enabled in-form snippet. */
(function (root) {
  'use strict';
  if (root.AMProductOptionsStorefront) { root.AMProductOptionsStorefront.mountAll(); return; }
  const states = new WeakMap();
  const GROUP = '_am_options_group', ROLE = '_am_options_role', RATIO = '_am_options_ratio';
  const put = (target, key, value) => Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true });
  const core = () => root.AMProductOptions;
  const renderer = () => root.AMProductOptionsRender;
  function route(action) {
    const prefix = root.Shopify?.routes?.root || '/';
    if (!/^\/(?!\/)[\w/\-]*$/.test(prefix)) throw new Error('Invalid Shopify locale route.');
    return prefix.replace(/\/?$/, '/') + 'cart/' + action + '.js';
  }
  function cartGetRoute() { return route('add').replace(/cart\/add\.js$/, 'cart.js'); }
  async function request(url, body, timeout = 15000) {
    const controller = new root.AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
        const response = await root.fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', signal: controller.signal,
          headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}) });
        const data = await response.json();
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Shopify could not update the cart.');
        return data;
        })(),
        new Promise((resolve, reject) => { timer = root.setTimeout(() => { controller.abort(); reject(new Error('Cart request timed out.')); }, timeout); }),
      ]);
    } finally { root.clearTimeout(timer); }
  }
  function catalogueProduct(raw) {
    if (!raw || !Array.isArray(raw.variants)) throw new Error('Product option catalogue is missing.');
    if (!/^\d+$/.test(String(raw.id))) throw new Error('A linked product could not be loaded from Shopify.');
    return { ...raw, id: String(raw.id), product_type: raw.product_type || raw.productType || '',
      variants: raw.variants.map(v => {
        if (!/^\d+$/.test(String(v.id))) throw new Error('A real Shopify variant ID is required.');
        const price = Number.isSafeInteger(v.priceCents) && v.priceCents >= 0 ? (v.priceCents / 100).toFixed(2) : v.price;
        core().priceToCents(price);
        if (typeof v.available !== 'boolean') throw new Error('Product option availability is unknown.');
        return { ...v, id: String(v.id), price, available: v.available };
      }) };
  }
  function legacyConflict(form) {
    const apoIsolated = root.AMProductOptionsLegacyIsolation?.apo === true && root.mwApoInit?.is_used === true;
    return Boolean(form.querySelector('.bold_options,[id^="hulkapps_custom_options_"],.mw-apo-options,.mw-apo-container,.mw-option,[data-mw-apo-option]') ||
      [...root.document.querySelectorAll('script[src]')].some(script =>
        /options\.shopapps\.site|shappify\.com\/apps\/options|hulk.*product.*options/i.test(script.src) ||
        (/apo-product-options/i.test(script.src) && !apoIsolated)));
  }
  function nativeValue(form, name, fallback) {
    const entry = form.elements.namedItem(name);
    return entry && typeof entry.value === 'string' ? entry.value : fallback;
  }
  function restoreButtons(state) {
    if (state.locked || state.busy || state.completed) return;
    const variant = state.product?.variants.find(item => item.id === nativeValue(state.form, 'id', ''));
    // A selection change must not re-enable a theme button for a sold-out variant.
    if (variant?.available !== true) return;
    (state.disabledButtons || []).forEach(button => { button.disabled = false; });
  }
  function setBusy(state, busy) {
    state.busy = busy;
    state.mount.setAttribute('aria-busy', String(busy));
    // Preserve theme inventory-driven disabled buttons; only restore our own changes.
    if (busy) {
      state.disabledButtons = [...state.form.querySelectorAll('button[type="submit"],input[type="submit"]')].filter(button => !button.disabled);
      state.disabledButtons.forEach(button => { button.disabled = true; });
      state.formWasInert = state.form.hasAttribute('inert');
      state.form.setAttribute('inert', '');
      state.frozenControls = [...state.form.querySelectorAll('input:not([type="submit"]),select,textarea,button:not([type="submit"])')].map(control => {
        const saved = { control, disabled: control.disabled, value: control.type === 'file' ? undefined : control.value, checked: control.checked };
        control.disabled = true;
        return saved;
      });
    } else {
      for (const saved of state.frozenControls || []) {
        // Theme scripts can alter disabled controls; restore the submitted snapshot.
        if (saved.value !== undefined) saved.control.value = saved.value;
        if (saved.checked !== undefined) saved.control.checked = saved.checked;
        saved.control.disabled = Boolean(state.locked) || saved.disabled;
      }
      state.frozenControls = null;
      if (!state.formWasInert) state.form.removeAttribute('inert');
      restoreButtons(state);
    }
  }
  function message(state, text, error = true) {
    state.status.textContent = text;
    state.status.classList.toggle('am-option-error', error);
    state.status.setAttribute('role', error ? 'alert' : 'status');
  }
  function lock(state, text) {
    state.locked = true;
    message(state, text);
    [...state.form.querySelectorAll('button[type="submit"],input[type="submit"]')].forEach(button => { button.disabled = true; });
  }
  function readControls(state) {
    const sections = [...state.fields.children];
    for (const [setIndex, set] of core().applicableSets(state.config.optionSets, state.product).entries()) {
      for (const field of set.fields) {
        const box = [...(sections[setIndex]?.querySelectorAll('[data-option-field]') || [])].find(node => node.dataset.optionField === field.id);
        if (!box) continue;
        const control = box.querySelector('input,select,textarea');
        if (!control) continue;
        const value = field.type === 'checkbox' ? [...box.querySelectorAll('input:checked')].map(input => input.value) : field.type === 'radio' ? (box.querySelector('input:checked')?.value || '') : control.value;
        if (!Object.prototype.hasOwnProperty.call(state.selections, set.id)) put(state.selections, set.id, Object.create(null));
        put(state.selections[set.id], field.id, value);
      }
    }
  }
  function quote(state, groupId) {
    return core().quote({ sets: state.config.optionSets, product: state.product, catalogue: state.catalogue,
      selections: state.selections, variantId: nativeValue(state.form, 'id', ''),
      quantity: Number(nativeValue(state.form, 'quantity', '1')), groupId });
  }
  function refresh(state) {
    if (state.locked || state.busy) return;
    try {
    const result = quote(state);
    const selections = root.JSON.parse(root.JSON.stringify(result.normalizedSelections));
    // Preserve raw visible edit buffers; normalize only the quote and cart payload.
    for (const { setId, fieldId } of result.visibleFields) {
      if (Object.prototype.hasOwnProperty.call(state.selections[setId] || {}, fieldId)) {
        if (!Object.prototype.hasOwnProperty.call(selections, setId)) put(selections, setId, Object.create(null));
        put(selections[setId], fieldId, state.selections[setId][fieldId]);
      }
    }
    state.selections = selections;
    renderer().render(state.fields, { sets: state.config.optionSets, product: state.product, catalogue: state.catalogue,
      selections: state.selections, quote: result, onChange(setId, fieldId, value) {
        if (state.busy || state.locked) return;
        if (!Object.prototype.hasOwnProperty.call(state.selections, setId)) put(state.selections, setId, Object.create(null));
        put(state.selections[setId], fieldId, value);
        state.completed = false;
        restoreButtons(state);
        message(state, '', false); refresh(state);
      } });
    state.total.textContent = 'Total Extras: ' + core().money(result.extrasTotalCents, state.config.currency);
    state.lastQuote = result;
    } catch (error) { lock(state, 'Product options could not be validated. Please contact the store.'); }
  }
  function sameGroup(cart, expected) {
    if (!cart || !Array.isArray(cart.items)) return false;
    const groupId = expected[0].properties[GROUP];
    const lines = cart.items.filter(line => line.properties?.[GROUP] === groupId);
    if (lines.length !== expected.length) return false;
    const base = lines.find(line => line.properties?.[ROLE] === 'base');
    if (!base?.key) return false;
    return expected.every(item => {
      const line = lines.find(line => String(line.variant_id || line.id) === String(item.id) && line.properties?.[ROLE] === item.properties[ROLE]);
      if (!line || line.quantity !== item.quantity) return false;
      if (!Object.entries(item.properties).every(([key, value]) => String(line.properties[key] ?? '') === String(value))) return false;
      return item.properties[ROLE] !== 'extra' || line.parent_relationship?.parent_key === base.key;
    });
  }
  function markAdded(state, cart, recovered) {
    state.completed = true;
    message(state, recovered ? 'Added to cart. The cart was checked after a connection error.' : 'Added to cart.', false);
    const link = root.document.createElement('a'); link.href = cartGetRoute().replace(/\.js$/, ''); link.textContent = ' View cart';
    state.status.append(link);
    state.form.dispatchEvent(new root.CustomEvent('am:product-options-added', { bubbles: true, detail: { cart, groupId: state.groupId } }));
  }
  async function submit(state) {
    if (state.busy || state.locked || state.completed) return;
    if (legacyConflict(state.form)) { lock(state, 'Product options are not ready: existing options apps still overlap this product. Please contact the store.'); return; }
    readControls(state);
    state.groupId = 'am-' + root.crypto.randomUUID();
    const result = quote(state, state.groupId);
    if (!result.valid) { refresh(state); message(state, result.errors.find(error => !error.fieldId)?.message || 'Please complete the required options before adding to cart.'); state.fields.querySelector('[aria-invalid="true"]')?.focus(); return; }
    if (nativeValue(state.form, 'selling_plan', '')) { lock(state, 'Subscription options are not supported by this product configuration. Please contact the store.'); return; }
    const items = result.items.map((item, index) => ({ ...item, properties: { ...item.properties }, ...(index ? { parent_id: result.items[0].id } : {}) }));
    if (items.slice(1).some(item => String(item.id) === String(items[0].id))) { lock(state, 'A product cannot be its own add-on. Please contact the store.'); return; }
    // Preserve ordinary theme properties (for example Braum seat selections), without replacing our group or validated fields.
    const existing = {};
    for (const [key, value] of new root.FormData(state.form)) {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      if (match && typeof value === 'string' && !match[1].startsWith('_am_options_')) Object.defineProperty(existing, match[1], { value, enumerable: true });
    }
    items[0].properties = { ...existing, ...items[0].properties };
    items.slice(1).forEach(item => { item.properties[RATIO] = String(item.quantity / items[0].quantity); });
    setBusy(state, true); message(state, 'Adding to cart…', false);
    try {
      await request(route('add'), { items }, state.timeout);
      const cart = await request(cartGetRoute(), null, state.timeout);
      if (!sameGroup(cart, items)) throw new Error('The cart does not contain the complete product and its extras.');
      markAdded(state, cart, false);
    } catch (error) {
      // Shopify stock errors and connection failures may still change the cart. Never retry or roll back blindly.
      try {
        const cart = await request(cartGetRoute(), null, state.timeout);
        if (sameGroup(cart, items)) { markAdded(state, cart, true); return; }
      } catch (_) { /* Keep the original uncertain result blocked. */ }
      lock(state, 'We could not confirm the complete product and extras. Some items may already be in your cart. View your cart before trying again; do not submit this form again.');
      const link = root.document.createElement('a'); link.href = cartGetRoute().replace(/\.js$/, ''); link.textContent = ' View cart'; state.status.append(link);
    } finally { setBusy(state, false); }
  }
  function mount(node, suppliedConfig) {
    if (!node || node.dataset.amOptionsEnabled !== 'true') return null;
    const form = node.closest('form');
    if (!form || !/\/cart\/add(?:\.js)?\/?$/.test(new root.URL(form.action, root.location.href).pathname)) return null;
    if (states.has(form)) return states.get(form);
    let supplied = suppliedConfig;
    try { if (!supplied) supplied = JSON.parse(node.querySelector('script[data-am-options-config]')?.textContent || 'null'); } catch (_) { /* Invalid opted configuration is blocked below. */ }
    if (supplied?.version === 1 && Array.isArray(supplied.optionSets) && !supplied.optionSets.length) { node.dataset.amOptionsEnabled = 'false'; return null; }
    const state = { mount: node, form, selections: Object.create(null), timeout: 15000 };
    state.fields = node.querySelector('[data-am-options-fields]') || node.appendChild(root.document.createElement('div'));
    state.total = node.querySelector('[data-am-options-total]') || node.appendChild(root.document.createElement('p'));
    state.status = node.querySelector('[data-am-options-status]') || node.appendChild(root.document.createElement('p'));
    states.set(form, state);
    form.dataset.amProductOptions = 'active';
    // Do not allow accelerated payment to bypass the validated parent/add-on add request.
    const suppressCheckout = () => form.querySelectorAll('.shopify-payment-button,.dynamic-checkout,[data-shopify="payment-button"]').forEach(control => { control.hidden = true; control.setAttribute('inert', ''); control.querySelectorAll('button,input').forEach(button => { button.disabled = true; }); });
    suppressCheckout();
    state.observer = new root.MutationObserver(() => {
      if (!root.document?.documentElement || !form.isConnected) { state.observer.disconnect(); return; }
      suppressCheckout();
      if (!state.locked && legacyConflict(form)) lock(state, 'Product options are not ready: existing options apps still overlap this product. Please contact the store.');
    });
    state.observer.observe(form, { childList: true, subtree: true });
    try {
      if (!core() || !renderer()) throw new Error('Product option scripts are missing.');
      state.config = supplied;
      if (state.config?.version !== 1 || state.config.error || state.config.currency !== 'CAD') throw new Error(state.config?.error || 'Product options need a supported CAD configuration.');
      if (root.Shopify?.currency?.active && root.Shopify.currency.active !== state.config.currency) throw new Error('Product options are currently available only in CAD.');
      state.product = catalogueProduct(state.config.product);
      state.catalogue = (state.config.catalogue || []).map(catalogueProduct);
      if (!core().applicableSets(state.config.optionSets, state.product).length) throw new Error('No enabled product option set matches this product.');
      if (legacyConflict(form)) {
        const error = new Error('Existing options apps still overlap this product. Complete the migration before enabling this form.');
        error.code = 'legacy-conflict'; throw error;
      }
      refresh(state);
      const nativeChanged = event => {
        if (state.busy || state.locked) return;
        if (!node.contains(event.target) && ['id', 'quantity'].includes(event.target.name) && (event.type !== 'input' || event.target.name === 'quantity')) {
          state.completed = false; restoreButtons(state); message(state, '', false); refresh(state);
        }
      };
      form.addEventListener('change', nativeChanged);
      form.addEventListener('input', nativeChanged);
    } catch (error) {
      const handle = state.product?.handle;
      const otherPage = typeof handle === 'string' && /^[a-z0-9][a-z0-9\-]*$/.test(handle) &&
        !root.location.pathname.replace(/\/$/, '').endsWith('/products/' + handle);
      if (error.code === 'legacy-conflict' && otherPage) {
        lock(state, "Choose this product's options on its product page.");
        const link = root.document.createElement('a');
        link.href = cartGetRoute().replace(/cart\.js$/, 'products/' + handle); link.textContent = ' Configure product'; state.status.append(link);
      } else lock(state, 'Product options are not ready: ' + error.message);
    }
    return state;
  }
  function mountAll() { root.document.querySelectorAll('[data-am-options-enabled="true"]').forEach(node => mount(node)); }
  // Window capture runs before the existing theme's form AJAX listener. Unrelated forms are untouched.
  root.addEventListener('submit', event => {
    const state = states.get(event.target);
    if (!state) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void submit(state).catch(() => lock(state, 'Product options could not be validated. Please contact the store.'));
  }, true);
  async function updateGroup(cart, groupId, quantity, options = {}) {
    if (!cart || !Array.isArray(cart.items) || typeof groupId !== 'string' || !groupId || !Number.isSafeInteger(quantity) || quantity < 0 || quantity > core().MAX_QUANTITY) throw new Error('Choose a valid product option group and quantity.');
    // Refresh keys immediately before mutation; no stale variant IDs or line positions are used.
    const current = await request(cartGetRoute(), null, options.timeout);
    const grouped = current.items?.filter(line => line.properties?.[GROUP] === groupId) || [];
    const bases = grouped.filter(line => line.properties?.[ROLE] === 'base');
    if (bases.length !== 1 || !bases[0].key) throw new Error('The option group could not be identified safely. Reload the cart.');
    const base = bases[0];
    const children = current.items.filter(line => line.parent_relationship?.parent_key === base.key);
    if (grouped.some(line => line !== base && !children.includes(line))) throw new Error('The option group has an invalid parent relationship. Reload the cart.');
    const updates = { [base.key]: quantity };
    for (const child of children) {
      const ratio = Number(child.properties?.[RATIO]);
      if (!child.key || (quantity > 0 && (child.properties?.[GROUP] !== groupId || child.properties?.[ROLE] !== 'extra' || !Number.isSafeInteger(ratio) || ratio < 1 || ratio * quantity > core().MAX_QUANTITY))) throw new Error('The add-on quantity ratio is invalid. Reload the cart.');
      updates[child.key] = quantity === 0 ? 0 : ratio * quantity;
    }
    // One keyed request includes parent and every child; a failure is surfaced and never retried.
    const updated = await request(route('update'), { updates }, options.timeout);
    const expected = [base, ...children].map(line => ({ id: String(line.variant_id || line.id), quantity: updates[line.key], properties: line.properties }));
    if (quantity === 0 ? !Array.isArray(updated.items) || updated.items.some(line => line.properties?.[GROUP] === groupId || updates[line.key] === 0) : !sameGroup(updated, expected)) {
      throw new Error('The complete cart quantity change was not confirmed. Inspect the cart before trying again.');
    }
    return updated;
  }
  root.AMProductOptionsStorefront = Object.freeze({ mount, mountAll, updateGroup });
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', mountAll, { once: true }); else mountAll();
  root.document.addEventListener('shopify:section:load', mountAll);
  // Quick views and asynchronously loaded product sections need the same guarded form.
  new root.MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches?.('[data-am-options-enabled="true"]') || node.querySelector?.('[data-am-options-enabled="true"]'))))) mountAll();
  }).observe(root.document.documentElement, { childList: true, subtree: true });
}(window));
